import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { EvaluationCompleteToast } from "@/components/context-console/EvaluationCompleteToast";
import { ROUTES } from "@/constants";
import {
  dismissToast,
  showToast,
  TOAST_CUSTOM_CLASS_NAMES,
  TOAST_DURATIONS,
  TOAST_STYLES,
} from "@/helpers/toast.helpers";

const CONTEXT_CONSOLE_PATH = ROUTES.SETTINGS_CONTEXT_CONSOLE;

/** What a settled request tells the admin, as a toast with a "view" action. */
export interface CompletionToastOptions {
  /** Same id replaces an earlier toast of the same kind instead of stacking. */
  toastId: string;
  success: boolean;
  title: string;
  description: string;
  viewLabel: string;
  /** Runs when the admin clicks "view", before the toast is dismissed. */
  onView?: () => void;
}

/**
 * Returns a function that shows a completion toast for a request that settled
 * while the admin was somewhere else. Does nothing while they're on the
 * Context Console page: the page renders the result (or the failure) inline,
 * and a toast on top would duplicate it. The toast stays until it is closed or
 * viewed; "view" navigates back to Context Console. Returns whether it
 * actually showed one, so a caller with its OWN on-page fallback (e.g. a
 * failure toast the page shows inline once it's back) can skip that fallback
 * when this one already said the same thing.
 *
 * Shared by the evaluate, live-set backfill and publish providers, which used
 * to each carry their own copy of this.
 */
export const useCompletionToast = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  return useCallback(
    ({ toastId, success, title, description, viewLabel, onView }: CompletionToastOptions): boolean => {
      if (locationRef.current.startsWith(CONTEXT_CONSOLE_PATH)) return false;

      const handleView = () => {
        navigate(CONTEXT_CONSOLE_PATH);
        onView?.();
        dismissToast(toastId);
      };

      showToast(
        <EvaluationCompleteToast
          variant={success ? "success" : "error"}
          title={title}
          description={description}
          viewLabel={viewLabel}
          onView={handleView}
          onDismiss={() => dismissToast(toastId)}
        />,
        {
          id: toastId,
          duration: TOAST_DURATIONS.LOADING, // Infinity — stays until closed or viewed.
          style: success ? TOAST_STYLES.success : TOAST_STYLES.error,
          classNames: TOAST_CUSTOM_CLASS_NAMES,
        },
      );
      return true;
    },
    [navigate],
  );
};

/**
 * Calls `reset` when the signed-in identity changes from one real user to
 * another, so a same-tab account switch never leaves one company's in-memory
 * results for the next admin.
 *
 * `"anon"` is the transient sentinel for "auth hasn't resolved yet" (every
 * mount, including a same-user page refresh, starts there), so only a
 * transition away from an already-known real identity counts as an account
 * switch. `reset` must be stable (a `useCallback`), so this only re-fires when
 * the identity changes.
 */
export const useResetOnIdentityChange = (identityKey: string, reset: () => void): void => {
  const identityRef = useRef(identityKey);
  useEffect(() => {
    const previous = identityRef.current;
    identityRef.current = identityKey;
    if (previous !== "anon" && previous !== identityKey) reset();
  }, [identityKey, reset]);
};
